/**
 * templateStore - the user's Office template library.
 *
 * Metadata lives in users/{uid}/workspace/templates (owner-writable doc):
 *   { items: [{ id, name, fileName, storagePath, downloadUrl, bytes,
 *     uploadedAt }], activeTemplateIds: {powerpoint, excel, word} }
 * The binary Office files live in Storage under
 * users/{uid}/templates/... (owner-scoped by storage.rules).
 *
 * The sidebar Workflows tab manages the library; the chat send path reads
 * the ACTIVE template through the cached getter below and injects it into
 * the agent's context so "start from scratch" pulls the user's template.
 * The FIRM default template is an admin/console concern (backend-set), not
 * part of this per-user library.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const CACHE_TTL_MS = 60000;
let cache = { key: null, at: 0, active: null };

const EXTENSIONS_BY_APP = {
  powerpoint: ['.pptx', '.potx'],
  excel: ['.xlsx', '.xltx'],
  word: ['.docx', '.dotx'],
};

const matchesApp = (template, app) => {
  const name = String(template?.fileName || template?.name || '').toLowerCase();
  return (EXTENSIONS_BY_APP[app] || EXTENSIONS_BY_APP.powerpoint).some((ext) => name.endsWith(ext));
};

/**
 * The user's selected template for one Office host
 * ({id,name,fileName,downloadUrl,...}) or null. An explicit 'none' means use
 * the open file; an explicit item id means that upload. 'firm-default' resolves
 * to the firm's template. No selection also means use the open file.
 */
export async function getActiveTemplate(userId, app = 'powerpoint') {
  if (!userId || !db) return null;
  const now = Date.now();
  const cacheKey = `${userId}:${app}`;
  if (cache.key === cacheKey && now - cache.at < CACHE_TTL_MS) return cache.active;
  try {
    const snap = await getDoc(doc(db, 'users', userId, 'workspace', 'templates'));
    const data = snap.exists() ? (snap.data() || {}) : {};
    const items = Array.isArray(data.items) ? data.items : [];
    const selection = data.activeTemplateIds?.[app]
      || (app === 'powerpoint' ? data.activeTemplateId : '')
      || '';
    const firm = data.firmTemplate?.downloadUrl && matchesApp(data.firmTemplate, app)
      ? { id: 'firm-default', fileName: data.firmTemplate.fileName || '', ...data.firmTemplate }
      : null;
    const compatibleItems = items.filter((item) => matchesApp(item, app));

    let active = null;
    if (!selection || selection === 'none') {
      active = null;
    } else if (selection === 'firm-default') {
      active = firm;
    } else {
      active = compatibleItems.find((t) => t?.id && t.id === selection) || null;
    }
    cache = { key: cacheKey, at: now, active };
    return active;
  } catch {
    return null;
  }
}

/** Drop the cache after the library changes so the next send sees it. */
export function invalidateTemplateCache() {
  cache = { key: null, at: 0, active: null };
}
